import { detectContactInfo } from './contactInfoDetection';
import { detectProductMentions, createProductCard, formatOpenAIResponseWithProducts as formatOpenAIResponseWithProductsFromDetection } from './productDetection';
import type { Product } from '../hooks/useProductSystem';

export function formatOpenAIResponse(text: string) {
  if (!text) return '';
  
  // Détecter les contacts dans le texte
  const contacts = detectContactInfo(text, '');
  let formattedText = text;

  // Mettre en évidence les contacts détectés avec un style plus visible
  contacts.forEach(contact => {
    const value = contact.value;
    const icon = contact.type === 'email' ? '📧' : '📱';
    formattedText = formattedText.replace(
      value,
      `<span class="inline-flex items-center gap-2 px-2 py-1 rounded-md bg-red-500/20 text-red-500 font-bold border border-red-500/30 shadow-sm">
        ${icon} ${value}
      </span>`
    );
  });

  // Détecter et rendre cliquables les URLs
  const urlRegex = /(https?:\/\/[^\s<]+[^<.,:;"')\]\s])/g;
  formattedText = formattedText.replace(urlRegex, (url) => {
    return `<a href="${url}" target="_blank" rel="noopener noreferrer" class="text-indigo-400 hover:text-indigo-300 underline break-all">${url}</a>`;
  });

  // Remplacer les ** par des balises span avec gradient
  const boldRegex = /\*\*(.*?)\*\*/g;
  formattedText = formattedText.replace(
    boldRegex, 
    '<span class="font-bold bg-clip-text text-transparent bg-gradient-to-r from-[var(--gradient-start)] to-[var(--gradient-end)]">$1</span>'
  );

  // Gérer les listes et paragraphes
  const lines = formattedText.split('\n');
  let finalHtml = '';
  let inList = false;

  lines.forEach(line => {
    const trimmedLine = line.trim();
    if (trimmedLine.startsWith('-') || trimmedLine.startsWith('*')) {
      if (!inList) {
        finalHtml += '<ul class="list-disc list-inside space-y-0.5">';
        inList = true;
      }
      finalHtml += `<li>${trimmedLine.substring(1).trim()}</li>`;
    } else {
      if (inList) {
        finalHtml += '</ul>';
        inList = false;
      }
      if (trimmedLine) {
        finalHtml += `<p class="mb-2">${trimmedLine}</p>`;
      } else {
        finalHtml += '<br/>';
      }
    }
  });

  if (inList) {
    finalHtml += '</ul>';
  }

  return finalHtml;
}

/**
 * Formate la réponse OpenAI et ajoute des vignettes pour les produits mentionnés
 * @param text Le texte à formater
 * @param chatbotPublicId L'identifiant du chatbot
 * @param mentionedProducts Les produits mentionnés (optionnel, sinon ils seront détectés)
 * @returns Le HTML formaté avec les vignettes de produits
 */
export async function formatOpenAIResponseWithProducts(
  text: string, 
  chatbotPublicId: string,
  mentionedProducts?: Product[]
): Promise<string> {
  // Déléguer à l'implémentation dans productDetection.ts pour utiliser la méthode unique
  return formatOpenAIResponseWithProductsFromDetection(text, chatbotPublicId, mentionedProducts);
}